"use client";

import * as React from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn-components/ui/tooltip";
import { useFeatureFlag } from "@/lib/feature-flags/useFeatureFlag";
import type { FeatureFlagKey } from "@/lib/feature-flags/flags";

interface FeatureGateProps {
  /** Flag that controls whether the child renders live. */
  flag: FeatureFlagKey;
  /**
   * The control to gate. When the flag is off it is rendered inside a
   * non-interactive wrapper with a "coming soon" tooltip instead of being
   * silently disabled. When on, it renders untouched.
   */
  children: React.ReactNode;
  /**
   * Optional live control to render when the flag is on. If omitted,
   * `children` is used for both states.
   */
  enabledChildren?: React.ReactNode;
  /** Override the tooltip text (defaults to the flag's reason). */
  reason?: string;
  className?: string;
}

/**
 * Honest gate for deferred controls. Instead of shipping a bare `disabled`
 * button with no explanation, wrap it here: when the flag is off the child is
 * shown disabled with a hover tooltip explaining the deferral, so users know
 * it is coming — not broken.
 */
export function FeatureGate({
  flag,
  children,
  enabledChildren,
  reason,
  className,
}: FeatureGateProps) {
  const { enabled, reason: flagReason } = useFeatureFlag(flag);

  if (enabled) {
    return <>{enabledChildren ?? children}</>;
  }

  const tip = reason ?? flagReason ?? "Coming soon";

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* wrapper keeps the tooltip working even when the child is disabled */}
          <span
            className={className}
            aria-disabled="true"
            data-feature-gated="true"
          >
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">{tip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default FeatureGate;
